import React, { useState, useRef, useEffect } from 'react';
import {
  Flex,
  Box,
  Text,
  Input,
  Button,
  VStack,
  HStack,
  Radio,
  RadioGroup,
  Stack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  useToast,
  Image
} from '@chakra-ui/react';
import { ChatList as ChatListComponent } from 'react-chat-elements';
import 'react-chat-elements/dist/main.css';
import { FiExternalLink, FiImage, FiSend } from 'react-icons/fi';

// React Big Calendar and moment imports
import { Calendar, momentLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import moment from 'moment';

// Import custom components
import TimeRangeWidget from '../widget/TimeRangeWidget';
import DatePickerLabel from '../widget/DatePickerLabel'; // Adjust path as needed
import { ChatIcon, LinkIcon } from '@chakra-ui/icons';
import { useAuth } from '../../hooks/AuthProvider';
import { API_BASE_URL } from '../../config/apiConfig';
import io from 'socket.io-client';
import { useMemo } from 'react';


function ChatList() {
  // Set up localizer for react-big-calendar
  const [isParticipantModalOpen, setIsParticipantModalOpen] = useState(false);
  const [newParticipantEmail, setNewParticipantEmail] = useState('');
  const localizer = momentLocalizer(moment);
  const toast = useToast();
  const auth = useAuth();
  const [participants, setParticipants] = useState([]);

  //const SOCKET_URL = "http://103.253.73.23:8822"; // your signaling server
  const SOCKET_URL = "https://socketio.dent.infovaley.com"
  let socket = null; // module-scoped singleton
  const [conversations, setConversations] = useState({});
  const scrollContainerRef = useRef(null);
  const [activeChat, setActiveChat] = useState(null);
  //const messages = activeChat ? conversations[activeChat.room_id] || [] : [];

  const messages = useMemo(() => {
    return activeChat ? conversations[activeChat.room_id] || [] : [];
  }, [conversations, activeChat]);

  useEffect(() => {
    if (auth?.user?.uid && auth?.user?.role) {
      fetchParticipants();
    }
  }, [auth?.user?.uid, auth?.user?.role]);

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
    }
  }, [messages]);
  
  
  const fetchParticipants = async () => {
    try {
      const token = auth.token;
      const uid = auth.user.uid;
      const role = auth.user.role;
  
      let endpoint = "";
      let payload = {};
  
      if (role === "doctor") {
        endpoint = "/list_participants_by_doctor_id";
        payload = { doctor_id: uid };
      } else if (role === "public") {
        endpoint = "/list_participants_by_user_id";
        payload = { user_id: uid };
      } else {
        console.warn("Unknown user role:", role);
        return;
      }
  
      const res = await fetch(`${API_BASE_URL}/api/site${endpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
  
      const data = await res.json();
      if (!res.ok) {
        console.error("Error loading participants:", data.error);
      } else {
        console.log("✅ Participants fetched:", data.participants);
        // set the fetched participants to state
        setParticipants(data.participants || []);
      }
    } catch (err) {
      console.error("❌ Failed to fetch participants:", err.message);
    }
  };

  // ------------------------------
  // Helper to validate URL
  // ------------------------------
  const isValidUrl = (url) => {
    try {
      new URL(url);
      return true;
    } catch (err) {
      return false;
    }
  };

  // Helper to convert URLs in text to clickable links.
  const linkifyText = (text) => {
    const urlRegex = /(https?:\/\/\S+)/g;
    return text.split(urlRegex).map((part, i) => {
      if (urlRegex.test(part)) {
        return (
          <a
            key={i}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'blue', textDecoration: 'underline' }}
          >
            {part}
          </a>
        );
      }
      return <span key={i}>{part}</span>;
    });
  };

  // Helper to get the image source.
  const getImageSrc = (imageData) => {
    if (imageData instanceof Blob) {
      return URL.createObjectURL(imageData);
    }
    return imageData;
  };


  const socketRef = useRef(null);
  const [inputValue, setInputValue] = useState('');
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [modalImage, setModalImage] = useState(null);
  const fileInputRef = useRef(null);

  // ------------------------------
  // Meeting Link Modal State (from Chat Header)
  // ------------------------------
  const [isMeetingLinkModalOpen, setIsMeetingLinkModalOpen] = useState(false);
  const [meetingType, setMeetingType] = useState("now"); // "now" or "later"
  const [meetingTitle, setMeetingTitle] = useState("");
  const [meetingDate, setMeetingDate] = useState(""); // "YYYY-MM-DD" (set via DatePickerLabel)
  const [meetingStartTime, setMeetingStartTime] = useState(""); // e.g., "09:00"
  const [meetingEndTime, setMeetingEndTime] = useState("");     // e.g., "10:00"
  const [meetingLink, setMeetingLink] = useState("");
  

  const modifiedParticipants = participants.map((p, index) => ({
    id: p.user_id || p.doctor_id,
    room_id: p.room_id,
    avatar: 'https://picsum.photos/seed/' + (p.user_id || p.doctor_id) + '/140/140',
    title: `${p.first_name || ''} ${p.last_name || ''}`,
    subtitle: p.latest_message?.content || '',
    date: new Date(p.latest_message?.timestamp || p.created_datetime),
    unread: p.latest_message?.is_read === 0 ? 1 : 0,
    className: activeChat && activeChat.id === (p.user_id || p.doctor_id) ? 'active-chat' : '',
  }));

  const handleChatHistory = (roomId, messages) => {
    console.log(`🔁 chat-history for room ${roomId}:`, messages);
    console.log(`user_id: ${auth.user.uid}`)

    const transformed = messages.map(m => ({
      position: `${m.sender_id}` === `${auth.user.uid}` ? 'right' : 'left',
      type: m.content_type === 'image' ? 'image' : 'text',
      text: m.content_type === 'text' ? m.content : '',
      image: m.content_type === 'image' ? m.content : null,
      date: new Date(m.timestamp),
    }));
  
    setConversations(prev => ({
      ...prev,
      [roomId]: transformed,
    }));
  };
  

  /*const handleChatMessage = (msg) => {
    const { senderId, text, imageUrl, timestamp } = msg;
  
    // We need activeChat.room_id to append to the correct conversation
    const roomId = activeChat?.room_id;
    if (!roomId) return;
  
    if(`${senderId}` !== `${auth.user.uid}`) {
      const newMessage = {
        position: `${senderId}` === `${auth.user.uid}` ? 'right' : 'left',
        type: imageUrl ? 'image' : 'text',
        text: text || '',
        image: imageUrl || null,
        date: new Date(timestamp),
      };
    
      setConversations(prev => ({
        ...prev,
        [roomId]: [...(prev[roomId] || []), newMessage]
      }));
    }

    console.log("📨 New message:", msg);
  };*/

  const handleChatMessage = (msg) => {
    const { roomId, senderId, text, imageUrl, timestamp } = msg;
    if (!roomId || !timestamp) return;
  
    // 🛡️ Avoid duplicate: skip if the message is from yourself
    if (senderId === auth.user.uid) return;
  
    const newMessage = {
      position: 'left',
      type: imageUrl ? 'image' : 'text',
      text: text || '',
      image: imageUrl || null,
      date: new Date(timestamp),
    };
  
    setConversations(prev => ({
      ...prev,
      [roomId]: [...(prev[roomId] || []), newMessage],
    }));
  
    if (roomId === activeChat?.room_id) {
      console.log("✅ New message added to visible room:", roomId);
    } else {
      console.log("📦 Message stored for background room:", roomId);
    }
  };  
  

  const handleChatItemClick = (item) => {
    const roomId = item.room_id;
    setActiveChat(item); 
  
    if (socketRef.current) {
      socketRef.current.disconnect();
    }
  
    const socket = io(SOCKET_URL, {
      transports: ['websocket'],
      reconnection: true,
      secure: true,
    });
  
    socketRef.current = socket;
  
    socket.on('connect', () => {
      console.log("✅ Connected to socket:", socket.id);
      socket.emit('join', roomId);
    });
  
    socket.on('chat-history', (messages) => handleChatHistory(roomId, messages));
    socket.on('chat-message', handleChatMessage);
  
    socket.on('connect_error', (err) => {
      console.error("❌ WebSocket error:", err);
    });
  };
  

  /*const handleSendMessage = () => {
    if (!inputValue.trim() || !activeChat) return;
    const newMessage = {
      position: 'right',
      type: 'text',
      text: inputValue,
      date: new Date(),
    };
    setConversations((prev) => ({
      ...prev,
      [activeChat.id]: [...(prev[activeChat.id] || []), newMessage],
    }));
    setInputValue('');
  };*/

  const handleSendMessage = () => {
    if (!inputValue.trim() || !activeChat) return;
  
    const roomId = activeChat.room_id;
    const senderId = auth.user.uid;
    const timestamp = new Date().toISOString();
  
    const newMessage = {
      position: 'right',
      type: 'text',
      text: inputValue,
      date: new Date(),
    };
  
    // Add to local conversation
    setConversations((prev) => ({
      ...prev,
      [roomId]: [...(prev[roomId] || []), newMessage],
    }));
  
    // Emit to server
    if (socketRef.current) {
      socketRef.current.emit('chat-message', {
        roomId,
        senderId,
        text: inputValue,
        timestamp,
      });
    }
  
    setInputValue('');
  };

  const handleUploadClick = () => {
    fileInputRef.current.click();
  };

  /*const handleImageChange = (e) => {
    if (!activeChat) return;
    const file = e.target.files[0];
    if (file) {
      // Store the Blob directly.
      const newImageMessage = {
        position: 'right',
        type: 'image',
        image: file,
        date: new Date(),
      };
      setConversations((prev) => ({
        ...prev,
        [activeChat.id]: [...(prev[activeChat.id] || []), newImageMessage],
      }));
      e.target.value = null;
    }
  };*/

  const handleImageChange = async (e) => {
    if (!activeChat || !socketRef.current) return;
    const file = e.target.files[0];
    if (!file) return;
  
    try {
      // ✅ Step 1: Prepare FormData
      const formData = new FormData();
      formData.append('image', file);
  
      // ✅ Step 2: Upload to server
      const res = await fetch(`${SOCKET_URL}/upload`, {
        method: 'POST',
        body: formData,
      });
  
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Upload failed');
  
      const imageUrl  = data.imageUrl;
      const timestamp = data.timestamp;
      const fileSize  = file.size;
      const roomId    = activeChat.room_id;
      const senderId  = auth.user.uid;
  
      // ✅ Step 3: Show image locally
      const newImageMessage = {
        position: 'right',
        type: 'image',
        image: imageUrl,
        date: new Date(timestamp),
      };
      setConversations((prev) => ({
        ...prev,
        [roomId]: [...(prev[roomId] || []), newImageMessage],
      }));
  
      // ✅ Step 4: Emit message to socket (same as Flutter)
      socketRef.current.emit('chat-message', {
        roomId,
        senderId,
        imageUrl,
        timestamp,
        size: fileSize,
      });
  
      e.target.value = null;
    } catch (err) {
      console.error('❌ Upload failed:', err);
      toast({
        title: 'อัปโหลดไม่สำเร็จ',
        description: err.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };
  

  const handleImageClick = (imageUrl) => {
    setModalImage(imageUrl);
    setIsImageModalOpen(true);
  };

  //const messages = activeChat ? conversations[activeChat.id] || [] : [];
  //const messages = activeChat ? conversations[activeChat.id] || [] : [];



  // ------------------------------
  // Calendar Agenda Handlers
  // ------------------------------
  const generateRandomString = (length) => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  const generateMeetingLink = () => {
    return `https://vroom.truevirtualworld.com/meeting-${generateRandomString(9)}`;
  }

  // ------------------------------
  // Meeting Link Modal Handlers (from Chat Header)
  // ------------------------------
  const openMeetingModal = () => {
    setMeetingType("now");
    setMeetingTitle("");
    setMeetingDate("");
    setMeetingStartTime("");
    setMeetingEndTime("");
    setMeetingLink(generateMeetingLink());
    setIsMeetingLinkModalOpen(true);
  };

  const handleMeetingSubmit = () => {
    if (!activeChat) return;
    let messageText = "";
    if (meetingType === "now") {
      messageText = `เริ่มประชุมตอนนี้ (คลิ๊กที่ลิ้งค์เพื่อเข้าประชุม): ${meetingLink}`;
    } else {
      if (!meetingTitle || !meetingDate || !meetingStartTime || !meetingEndTime) {
        toast({
          title: "Missing input",
          description: "กรุณาระบุหัวข้อประชุม, วันที่และเวลาประชุม",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      const meetingStart = new Date(`${meetingDate}T${meetingStartTime}`);
      const meetingEnd = new Date(`${meetingDate}T${meetingEndTime}`);
      messageText = `Scheduled meeting: ${meetingTitle}\nDate/Time: ${moment(meetingStart).format('LLL')} - ${moment(meetingEnd).format('LLL')}\nMeeting Link: ${meetingLink}`;

    }

    const newMeetingMessage = {
      position: 'right',
      type: 'text',
      text: messageText,
      date: new Date(),
    };
  
    const roomId = activeChat.room_id;
    const senderId = auth.user.uid;
    const timestamp = new Date().toISOString();
  
    // ✅ 1. Add to local conversation
    setConversations((prev) => ({
      ...prev,
      [roomId]: [...(prev[roomId] || []), newMeetingMessage],
    }));
  
    // ✅ 2. Emit via socket
    if (socketRef.current) {
      socketRef.current.emit('chat-message', {
        roomId,
        senderId,
        text: messageText,
        timestamp,
      });
    }

    setIsMeetingLinkModalOpen(false);
  };

  return (
    <Flex h="80vh" direction={['column', 'column', 'row']}>
      {/* Global styles to highlight the active chat list item */}
      <style jsx global>{`
        .active-chat {
          background-color: #e0f7fa !important;
        }
      `}</style>

      {/* Left Panel: Participants and Calendar */}
      <Box
  w={['100%', '100%', '40%']} // 100% width on mobile/tablet, 40% on desktop
  borderWidth="1px"
  borderColor="black"
  p={4}
  pr={[0, 0, 4]}  // remove right padding on mobile
  mb={[4, 4, 0]}  // add margin-bottom on mobile to separate from chat box
>
        <Flex justifyContent="space-between" alignItems="center" mb={4}>
          <Text fontSize="xl" fontWeight="bold" mb={4}>
            รายชื่อคู่สนทนา
          </Text>
          <Button
            mr={2}
            colorScheme="blue"
            size="sm"
            onClick={() => setIsParticipantModalOpen(true)}
          >
            เพิ่มรายชื่อ
          </Button>
        </Flex>
        <ChatListComponent
          dataSource={modifiedParticipants}
          onClick={handleChatItemClick}
        />
      </Box>

      {/* Right Panel: Chat Area */}
      <Box
  backgroundColor="blue.50"
  borderColor="black"
  borderWidth="1px"
  flex="1"
  display="flex"
  flexDirection="column"
>
        {activeChat ? (
          <>
            {/* Chat Header with Meeting Link Button */}
            <Box backgroundColor="white" borderBottom="1px solid #e2e8f0" pb={2} mb={4} px={4}>
              <Flex alignItems="center" justifyContent="space-between">
                <Text fontSize="2xl" fontWeight="bold">
                  {activeChat.title}
                </Text>
                <Button mt={2} size="sm" variant="outline" leftIcon={<LinkIcon />} colorScheme="teal" onClick={openMeetingModal}>
                  สร้างลิงค์ประชุม
                </Button>
              </Flex>
            </Box>

            {/* Messages Display */}
            <Box ref={scrollContainerRef} flex="1" overflowY="auto" mb={4}>
  <VStack spacing={4} align="stretch">
    {messages.map((msg, index) => (
      <Box
        key={index}
        alignSelf={msg.position === 'right' ? 'flex-end' : 'flex-start'}
        maxW="70%"
        bg={msg.position === 'right' ? 'blue.100' : 'gray.100'}
        p={3}
        borderRadius="md"
      >
        {msg.type === 'text' && <Text>{linkifyText(msg.text)}</Text>}
        {msg.type === 'image' && (
          <Image
            src={getImageSrc(msg.image)}
            alt="Sent image"
            maxH="300px"
            objectFit="cover"
            borderRadius="md"
            cursor="pointer"
            onClick={() => handleImageClick(getImageSrc(msg.image))}
          />
        )}
        <Text fontSize="xs" color="gray.500" textAlign="right">
          {msg.date.toLocaleTimeString()}
        </Text>
      </Box>
    ))}
  </VStack>
</Box>
            {/*<Box flex="1" overflowY="auto" mb={4}>
              <VStack spacing={4} align="stretch">
                {messages.map((msg, index) => (
                  <Box
                    key={index}
                    alignSelf={msg.position === 'right' ? 'flex-end' : 'flex-start'}
                    maxW="70%"
                    bg={msg.position === 'right' ? 'blue.100' : 'gray.100'}
                    p={3}
                    borderRadius="md"
                  >
                    {msg.type === 'text' && (
                      <Text>
                        {linkifyText(msg.text)}
                      </Text>
                    )}
                    {msg.type === 'image' && (
                      <Image
                        src={getImageSrc(msg.image)}
                        alt="Sent image"
                        maxH="300px"
                        objectFit="cover"
                        borderRadius="md"
                        cursor="pointer"
                        onClick={() => handleImageClick(getImageSrc(msg.image))}
                      />
                    )}
                    <Text fontSize="xs" color="gray.500" textAlign="right">
                      {msg.date.toLocaleTimeString()}
                    </Text>
                  </Box>
                ))}
              </VStack>
            </Box>*/}

            {/* Message Input */}
            <Flex
              bgColor="gray.200"
              my={1}
              mx={1}
              as="form"
              onSubmit={(e) => {
                e.preventDefault();
                handleSendMessage();
              }}
            >
              <Input
                bgColor="white"
                _placeholder={{ color: 'gray.300' }}
                placeholder="พิมพ์ข้อความ..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                mr={2}
              />
              <Button leftIcon={<FiSend />} type="submit" colorScheme="blue" mr={2}>
                ส่ง
              </Button>
              <Button leftIcon={<FiImage />} variant="outline" onClick={handleUploadClick} colorScheme="green">
                ภาพ
              </Button>
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleImageChange}
              />
            </Flex>
          </>
        ) : (
          <Flex align="center" justify="center" h="100%">
            <Text fontSize="lg" color="gray.500">
              Select a participant to start chatting
            </Text>
          </Flex>
        )}
      </Box>

      {/* Modal for Full-size Image Preview */}
      <Modal
        isOpen={isImageModalOpen}
        onClose={() => setIsImageModalOpen(false)}
        size="5xl"
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Image Preview</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {modalImage && (
              <Image src={modalImage} alt="Full-size image" width="100%" />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Modal for Creating/Scheduling Meeting Link (from Chat Header) */}
      <Modal
        size="2xl"
        isOpen={isMeetingLinkModalOpen}
        onClose={() => setIsMeetingLinkModalOpen(false)}
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>สร้างลิงค์ประชุม</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <RadioGroup value={meetingType} onChange={setMeetingType}>
                <Stack direction="row">
                  <Radio value="now">เริ่มประชุมตอนนี้</Radio>
                  <Radio value="later">ประชุมภายหลัง</Radio>
                </Stack>
              </RadioGroup>
              {meetingType === "later" && (
                <>
                  <Input
                    placeholder="หัวข้อประชุม..."
                    value={meetingTitle}
                    onChange={(e) => setMeetingTitle(e.target.value)}
                  />
                  <DatePickerLabel initDateStr={meetingDate} setSelectedDay={setMeetingDate} />
                  <Text>ช่วงเวลา:</Text>
                  <TimeRangeWidget
                    resetTime={false}
                    sHour={""}
                    sMinute={""}
                    eHour={""}
                    eMinute={""}
                    setStartTime={setMeetingStartTime}
                    setEndTime={setMeetingEndTime}
                  />
                </>
              )}
              <Input placeholder="Meeting Link" value={meetingLink} isReadOnly />
              <Button colorScheme="blue" onClick={handleMeetingSubmit}>
                ส่งลิงค์ประชุม
              </Button>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={isParticipantModalOpen}
        onClose={() => setIsParticipantModalOpen(false)}
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>เพิ่มผู้เข้าร่วม</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <Input
                placeholder="กรอกอีเมลผู้เข้าร่วม..."
                value={newParticipantEmail}
                onChange={(e) => setNewParticipantEmail(e.target.value)}
              />
              <Button
                colorScheme="blue"
                onClick={async () => {
                  if (!newParticipantEmail.trim()) {
                    toast({
                      title: "กรุณากรอกอีเมล",
                      status: "warning",
                      duration: 3000,
                      isClosable: true,
                    });
                    return;
                  }
                
                  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                  if (!emailRegex.test(newParticipantEmail)) {
                    toast({
                      title: "รูปแบบอีเมลไม่ถูกต้อง",
                      status: "error",
                      duration: 3000,
                      isClosable: true,
                    });
                    return;
                  }
                
                  try {
                    const userType = auth.user.role;
                    const uid = auth.user.uid;
                
                    let endpoint = "";
                    let payload = {};
                
                    if (userType === "doctor") {
                      endpoint = "/user_add_user_chat";
                      payload = {
                        user_email: newParticipantEmail,
                        doctor_id: uid,
                      };
                    } else if (userType === "public") {
                      endpoint = "/user_add_doctor_chat";
                      payload = {
                        user_id: uid,
                        doctor_email: newParticipantEmail,
                      };
                    } else {
                      toast({
                        title: "ไม่สามารถระบุประเภทผู้ใช้",
                        status: "error",
                        duration: 3000,
                        isClosable: true,
                      });
                      return;
                    }
                
                    const res = await fetch(`${API_BASE_URL}/api/site/${endpoint}`, {
                      method: "POST",
                      headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${auth.token}`,
                      },
                      body: JSON.stringify(payload),
                    });
                
                    const data = await res.json();
                
                    if (!res.ok) {
                      toast({
                        title: "เกิดข้อผิดพลาด",
                        description: data.error || "ไม่สามารถเพิ่มผู้เข้าร่วมได้",
                        status: "error",
                        duration: 3000,
                        isClosable: true,
                      });
                    } else {
                      toast({
                        title: "เพิ่มผู้เข้าร่วมสำเร็จ",
                        description: `Room ID: ${data.room_id}`,
                        status: "success",
                        duration: 3000,
                        isClosable: true,
                      });
                      setNewParticipantEmail('');
                      setIsParticipantModalOpen(false);
                      // You may refresh the participants list here if needed
                    }
                  } catch (err) {
                    console.error(err);
                    toast({
                      title: "เกิดข้อผิดพลาดภายใน",
                      status: "error",
                      duration: 3000,
                      isClosable: true,
                    });
                  }
                }}
                
              >
                เพิ่ม
              </Button>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>

    </Flex>
  );
}

export default ChatList;
